/**
 * Instagram Story Canvas Card Rendering Engine
 * Decoupled 1080x1920 (9:16 vertical story) graphic generator for workouts and splits.
 *
 * @module export/instagram
 */

import { calculateWorkoutZoneSeconds } from '../telemetry/index.js';
import { getWorkoutSufferScore } from '../performance/index.js';

/**
 * Draws a rounded rectangle path on a 2D canvas context.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x
 * @param {number} y
 * @param {number} width
 * @param {number} height
 * @param {number} radius
 * @param {string|CanvasGradient|null} [fillStyle]
 * @param {string|CanvasGradient|null} [strokeStyle]
 * @param {number} [strokeWidth=1]
 */
export function drawRoundedRect(ctx, x, y, width, height, radius, fillStyle = null, strokeStyle = null, strokeWidth = 1) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();

  if (fillStyle) {
    ctx.fillStyle = fillStyle;
    ctx.fill();
  }
  if (strokeStyle) {
    ctx.strokeStyle = strokeStyle;
    ctx.lineWidth = strokeWidth || 1;
    ctx.stroke();
  }
}

/**
 * Wraps text into multiple lines bounded by maxWidth.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {number} x
 * @param {number} y
 * @param {number} maxWidth
 * @param {number} lineHeight
 */
export function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  if (!text) return;
  const words = text.split(' ');
  let line = '';
  let currentY = y;

  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + ' ';
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;
    if (testWidth > maxWidth && n > 0) {
      ctx.fillText(line, x, currentY);
      line = words[n] + ' ';
      currentY += lineHeight;
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line, x, currentY);
}

function formatStageDuration(min, sec, stage) {
  let m = Number.isFinite(min) ? Math.max(0, Math.floor(min)) : null;
  let s = Number.isFinite(sec) ? Math.max(0, Math.round(sec)) : null;
  if ((m === null || m === 0) && (s === null || s === 0) && stage) {
    const rawSec = Number.isFinite(stage.duration_seconds)
      ? stage.duration_seconds
      : (Number.isFinite(stage.duration) ? (stage.duration > 100 ? stage.duration : Math.round(stage.duration * 60)) : null);
    if (Number.isFinite(rawSec) && rawSec > 0) {
      m = Math.floor(rawSec / 60);
      s = Math.round(rawSec % 60);
    }
  }
  const totalSecs = (m || 0) * 60 + (s || 0);
  const finalMin = Math.floor(totalSecs / 60);
  const finalSec = totalSecs % 60;
  return `${String(finalMin).padStart(2, '0')}:${String(finalSec).padStart(2, '0')}`;
}

function formatTotalDuration(duration) {
  if (!duration || !Number.isFinite(duration) || duration <= 0) return '00:00';
  const totalSecs = Math.round(duration * 60);
  const m = Math.floor(totalSecs / 60);
  const s = totalSecs % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Renders a high-resolution 1080x1920 Instagram Story graphic on a Canvas element.
 *
 * @param {Object} workout - Target workout data model.
 * @param {Object} [options={}] - Rendering options.
 * @param {HTMLCanvasElement} [options.canvas] - Optional existing canvas to render onto.
 * @param {number} [options.width=1080] - Canvas width in pixels.
 * @param {number} [options.height=1920] - Canvas height in pixels.
 * @param {boolean} [options.asBlob=false] - If true, returns Promise<Blob> instead of canvas.
 * @returns {HTMLCanvasElement|Promise<Blob>} The rendered canvas or Blob.
 */
export function renderInstagramCard(workout, options = {}) {
  if (!workout || typeof workout !== 'object') {
    throw new TypeError('renderInstagramCard requires a valid workout object');
  }

  const width = options.width || 1080;
  const height = options.height || 1920;

  let canvas = options.canvas;
  if (!canvas) {
    if (typeof document !== 'undefined' && document.createElement) {
      canvas = document.createElement('canvas');
    } else if (typeof OffscreenCanvas !== 'undefined') {
      canvas = new OffscreenCanvas(width, height);
    } else {
      throw new Error('Canvas rendering environment not available.');
    }
  }

  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Unable to acquire 2D rendering context');
  }

  // 1. Draw Background Gradient
  const bgGrad = ctx.createLinearGradient(0, 0, width, height);
  bgGrad.addColorStop(0, '#080711');
  bgGrad.addColorStop(1, '#151233');
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, width, height);

  // 2. Decorative Background Circles
  ctx.beginPath();
  ctx.arc(100, 200, 300, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(99, 102, 241, 0.05)';
  ctx.fill();

  ctx.beginPath();
  ctx.arc(980, 1700, 400, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(234, 179, 8, 0.03)';
  ctx.fill();

  // 3. Draw Branding Header
  ctx.textAlign = 'center';
  ctx.fillStyle = '#eab308'; // Neon Yellow
  ctx.font = "800 56px 'Outfit', 'Inter', sans-serif";
  ctx.fillText('⚡ ROXKIT', 540, 160);

  ctx.fillStyle = '#818cf8'; // Indigo
  ctx.font = "700 22px 'Outfit', 'Inter', sans-serif";
  ctx.fillText('HYPER HYBRID PERFORMANCE', 540, 210);

  // Decorative glowing line below header
  const headerLineGrad = ctx.createLinearGradient(200, 0, 880, 0);
  headerLineGrad.addColorStop(0, 'rgba(99, 102, 241, 0)');
  headerLineGrad.addColorStop(0.5, 'rgba(234, 179, 8, 0.5)');
  headerLineGrad.addColorStop(1, 'rgba(99, 102, 241, 0)');
  ctx.fillStyle = headerLineGrad;
  ctx.fillRect(200, 240, 680, 3);

  // 4. Workout Name & Date Card
  drawRoundedRect(ctx, 100, 280, 880, 170, 24, 'rgba(255, 255, 255, 0.02)', 'rgba(255, 255, 255, 0.06)', 2);

  ctx.fillStyle = '#ffffff';
  ctx.font = "800 46px 'Outfit', 'Inter', sans-serif";
  const workoutTitle = workout.notes && workout.notes.startsWith('Synced: "')
    ? workout.notes.replace('Synced: "', '').replace('"', '')
    : `${workout.type || 'Hyrox'} Workout`;
  ctx.fillText(workoutTitle.toUpperCase(), 540, 355);

  ctx.fillStyle = '#94a3b8'; // Slate
  ctx.font = "500 28px 'Outfit', 'Inter', sans-serif";
  ctx.fillText((workout.date || 'TODAY').toUpperCase(), 540, 410);

  // 5. Core Metrics (2x2 Grid)
  const cellW = 420;
  const cellH = 220;
  const cellRadius = 24;

  // Cell 1: DURATION
  drawRoundedRect(ctx, 100, 490, cellW, cellH, cellRadius, 'rgba(255, 255, 255, 0.03)', 'rgba(255, 255, 255, 0.08)', 2);
  ctx.textAlign = 'left';
  ctx.fillStyle = '#94a3b8';
  ctx.font = "700 20px 'Outfit', 'Inter', sans-serif";
  ctx.fillText('DURATION', 140, 545);
  ctx.fillStyle = '#ffffff';
  ctx.font = "800 52px 'Outfit', 'Inter', sans-serif";
  ctx.fillText(formatTotalDuration(workout.duration), 140, 635);

  // Cell 2: SUFFER SCORE
  drawRoundedRect(ctx, 560, 490, cellW, cellH, cellRadius, 'rgba(255, 255, 255, 0.03)', 'rgba(255, 255, 255, 0.08)', 2);
  ctx.fillStyle = '#94a3b8';
  ctx.font = "700 20px 'Outfit', 'Inter', sans-serif";
  ctx.fillText('SUFFER SCORE', 600, 545);

  const computedSuffer = typeof getWorkoutSufferScore === 'function' ? getWorkoutSufferScore(workout) : workout.sufferScore;
  ctx.fillStyle = '#eab308';
  ctx.font = "800 64px 'Outfit', 'Inter', sans-serif";
  ctx.fillText(computedSuffer !== null && computedSuffer !== undefined ? String(computedSuffer) : '--', 600, 620);

  let effortLabel = 'Active Effort';
  let effortColor = '#94a3b8';
  if (computedSuffer > 150) {
    effortLabel = 'Extreme Effort';
    effortColor = '#ef4444';
  } else if (computedSuffer > 80) {
    effortLabel = 'Hard Effort';
    effortColor = '#f97316';
  } else if (computedSuffer > 30) {
    effortLabel = 'Moderate Effort';
    effortColor = '#eab308';
  }
  ctx.fillStyle = effortColor;
  ctx.font = "600 20px 'Outfit', 'Inter', sans-serif";
  ctx.fillText(effortLabel.toUpperCase(), 600, 675);

  // Cell 3: DISTANCE
  drawRoundedRect(ctx, 100, 750, cellW, cellH, cellRadius, 'rgba(255, 255, 255, 0.03)', 'rgba(255, 255, 255, 0.08)', 2);
  ctx.fillStyle = '#94a3b8';
  ctx.font = "700 20px 'Outfit', 'Inter', sans-serif";
  ctx.fillText('DISTANCE', 140, 805);
  ctx.fillStyle = '#ffffff';
  ctx.font = "800 52px 'Outfit', 'Inter', sans-serif";
  ctx.fillText(workout.distance ? `${workout.distance} km` : 'N/A', 140, 895);

  // Cell 4: ENERGY BURNED
  drawRoundedRect(ctx, 560, 750, cellW, cellH, cellRadius, 'rgba(255, 255, 255, 0.03)', 'rgba(255, 255, 255, 0.08)', 2);
  ctx.fillStyle = '#94a3b8';
  ctx.font = "700 20px 'Outfit', 'Inter', sans-serif";
  ctx.fillText('ENERGY BURNED', 600, 805);
  ctx.fillStyle = '#ffffff';
  ctx.font = "800 52px 'Outfit', 'Inter', sans-serif";
  ctx.fillText(workout.calories ? `${workout.calories} kcal` : '--', 600, 895);

  // 6. Station Splits / Dynamic Content
  const tableY = 1010;
  const tableH = 680;
  drawRoundedRect(ctx, 100, tableY, 880, tableH, 28, 'rgba(255, 255, 255, 0.015)', 'rgba(255, 255, 255, 0.05)', 2);

  const stages = workout.stages || [];

  if (stages.length > 0) {
    ctx.textAlign = 'left';
    ctx.fillStyle = '#ffffff';
    ctx.font = "800 28px 'Outfit', 'Inter', sans-serif";
    ctx.fillText('STATION SPLITS', 140, tableY + 55);

    ctx.beginPath();
    ctx.arc(390, tableY + 45, 6, 0, Math.PI * 2);
    ctx.fillStyle = '#eab308';
    ctx.fill();

    let startRowY;
    let rowStep;

    const zoneTimes = calculateWorkoutZoneSeconds(workout);
    const totalZoneSecs = zoneTimes.reduce((a, b) => a + (b || 0), 0);
    const hasZones = totalZoneSecs > 0;

    if (hasZones) {
      const barX = 140;
      const barY = tableY + 85;
      const barW = 800;
      const barH = 20;

      drawRoundedRect(ctx, barX, barY, barW, barH, 10, 'rgba(255, 255, 255, 0.05)', null, 0);

      const zoneColors = ['#06b6d4', '#10b981', '#eab308', '#f97316', '#ef4444'];
      let currentX = barX;

      zoneTimes.forEach((secs, idx) => {
        const s = secs || 0;
        if (s <= 0) return;
        const segmentW = barW * (s / totalZoneSecs);

        ctx.fillStyle = zoneColors[idx];
        ctx.fillRect(currentX, barY, segmentW, barH);
        currentX += segmentW;
      });

      drawRoundedRect(ctx, barX, barY, barW, barH, 10, null, 'rgba(255, 255, 255, 0.15)', 2);

      ctx.font = "bold 16px 'Outfit', 'Inter', sans-serif";
      ctx.textAlign = 'center';

      let labelX = barX;
      zoneTimes.forEach((secs, idx) => {
        const s = secs || 0;
        if (s <= 0) return;
        const segmentW = barW * (s / totalZoneSecs);
        const midX = labelX + segmentW / 2;

        if (segmentW > 35) {
          ctx.fillStyle = '#ffffff';
          const pct = Math.round((s / totalZoneSecs) * 100);
          ctx.fillText(`Z${idx + 1}`, midX, barY + 38);
          ctx.fillStyle = '#94a3b8';
          ctx.fillText(`${pct}%`, midX, barY + 56);
        }
        labelX += segmentW;
      });

      startRowY = tableY + 215;
      rowStep = 55;
    } else {
      startRowY = stages.length > 8 ? (tableY + 115) : (tableY + 130);
      rowStep = stages.length > 8 ? 62 : 66;
    }

    if (stages.length > 8) {
      const col1X = 140;
      const col2X = 560;

      stages.forEach((stage, idx) => {
        if (idx >= 16) return;
        const isCol2 = idx >= 8;
        const colX = isCol2 ? col2X : col1X;
        const rowY = startRowY + (isCol2 ? idx - 8 : idx) * rowStep;

        ctx.fillStyle = '#eab308';
        ctx.font = "700 22px 'Outfit', 'Inter', sans-serif";
        ctx.fillText(`${idx + 1}.`, colX, rowY);

        let label = stage.stationName || stage.name || stage.type || 'Stage';
        if (label === 'RoxZone') label = 'RoxZone';
        ctx.fillStyle = '#ffffff';
        ctx.font = "500 22px 'Outfit', 'Inter', sans-serif";
        ctx.fillText(label.substring(0, 16), colX + 40, rowY);

        ctx.textAlign = 'right';
        ctx.fillStyle = '#818cf8';
        ctx.font = "700 22px 'Outfit', 'Inter', sans-serif";
        const valX = isCol2 ? 940 : 510;
        ctx.fillText(formatStageDuration(stage.durationMin, stage.durationSec, stage), valX, rowY);
        ctx.textAlign = 'left';
      });
    } else {
      stages.forEach((stage, idx) => {
        const rowY = startRowY + idx * rowStep;

        if (idx > 0) {
          ctx.strokeStyle = 'rgba(255,255,255,0.03)';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(140, rowY - 42);
          ctx.lineTo(940, rowY - 42);
          ctx.stroke();
        }

        ctx.fillStyle = '#eab308';
        ctx.font = "700 24px 'Outfit', 'Inter', sans-serif";
        ctx.fillText(`${idx + 1}.`, 140, rowY);

        let label = stage.stationName || stage.name || stage.type || 'Stage';
        if (label === 'RoxZone') label = 'RoxZone';
        ctx.fillStyle = '#ffffff';
        ctx.font = "500 24px 'Outfit', 'Inter', sans-serif";
        ctx.fillText(label, 190, rowY);

        ctx.textAlign = 'right';
        ctx.fillStyle = '#818cf8';
        ctx.font = "700 24px 'Outfit', 'Inter', sans-serif";
        ctx.fillText(formatStageDuration(stage.durationMin, stage.durationSec, stage), 940, rowY);
        ctx.textAlign = 'left';
      });
    }
  } else {
    const zoneTimes = calculateWorkoutZoneSeconds(workout);
    const totalZoneSecs = zoneTimes.reduce((a, b) => a + (b || 0), 0);
    if (totalZoneSecs > 0) {
      ctx.textAlign = 'left';
      ctx.fillStyle = '#ffffff';
      ctx.font = "800 28px 'Outfit', 'Inter', sans-serif";
      ctx.fillText('HEART RATE ZONES', 140, tableY + 55);

      const zoneLabels = [
        'Z1 Active Recovery',
        'Z2 Aerobic Endurance',
        'Z3 Tempo Stability',
        'Z4 Threshold',
        'Z5 Anaerobic Max'
      ];
      const zoneColors = ['#06b6d4', '#10b981', '#eab308', '#f97316', '#ef4444'];

      const maxBarW = 460;
      const startRowY = tableY + 125;
      const rowStep = 100;

      zoneLabels.forEach((label, idx) => {
        const rowY = startRowY + idx * rowStep;
        const timeSecs = Number.isFinite(zoneTimes[idx]) ? Math.max(0, zoneTimes[idx]) : 0;
        const totalZoneRounded = Math.round(timeSecs);
        const min = Math.floor(totalZoneRounded / 60);
        const sec = totalZoneRounded % 60;
        const timeStr = `${min}m ${sec}s`;
        const pct = totalZoneSecs > 0 ? (timeSecs / totalZoneSecs) : 0;

        ctx.fillStyle = '#ffffff';
        ctx.font = "700 20px 'Outfit', 'Inter', sans-serif";
        ctx.fillText(label, 140, rowY);

        drawRoundedRect(ctx, 140, rowY + 12, maxBarW, 20, 10, 'rgba(255, 255, 255, 0.05)', null, 0);
        if (pct > 0) {
          drawRoundedRect(ctx, 140, rowY + 12, Math.max(20, maxBarW * pct), 20, 10, zoneColors[idx], null, 0);
        }

        ctx.fillStyle = '#94a3b8';
        ctx.font = "700 20px 'Outfit', 'Inter', sans-serif";
        ctx.fillText(`${timeStr} (${Math.round(pct * 100)}%)`, 620, rowY + 28);
      });
    } else {
      ctx.textAlign = 'left';
      ctx.fillStyle = '#ffffff';
      ctx.font = "800 28px 'Outfit', 'Inter', sans-serif";
      ctx.fillText('PERFORMANCE INSIGHTS', 140, tableY + 60);

      ctx.fillStyle = '#94a3b8';
      ctx.font = "700 22px 'Outfit', 'Inter', sans-serif";
      ctx.fillText('AVG HEART RATE', 140, tableY + 140);
      ctx.fillText('PEAK HEART RATE', 140, tableY + 210);
      ctx.fillText('DEVICE SOURCE', 140, tableY + 280);
      ctx.fillText('INTENSITY MULTIPLIER', 140, tableY + 350);

      ctx.fillStyle = '#ffffff';
      ctx.font = "700 22px 'Outfit', 'Inter', sans-serif";
      ctx.fillText(workout.avgHeartrate ? `${workout.avgHeartrate} bpm` : '--', 500, tableY + 140);
      ctx.fillText(workout.maxHeartrate ? `${workout.maxHeartrate} bpm` : '--', 500, tableY + 210);
      ctx.fillText(workout.deviceName || 'iOS Device', 500, tableY + 280);
      ctx.fillText(workout.sufferMultiplier ? `${workout.sufferMultiplier}x` : '1x', 500, tableY + 350);

      const summaryText = workout.aiSummary || workout.notes;
      if (summaryText) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.02)';
        drawRoundedRect(ctx, 140, tableY + 410, 800, 210, 16, 'rgba(255, 255, 255, 0.02)', 'rgba(255,255,255,0.05)', 1);

        ctx.fillStyle = '#818cf8';
        ctx.font = "italic 500 22px 'Outfit', 'Inter', sans-serif";
        wrapText(ctx, `"${summaryText}"`, 170, tableY + 460, 740, 36);
      }
    }
  }

  // 7. Footer Section
  ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
  ctx.fillRect(200, 1750, 680, 1);

  ctx.textAlign = 'center';
  ctx.fillStyle = '#ffffff';
  ctx.font = "700 24px 'Outfit', 'Inter', sans-serif";
  ctx.fillText('TRAIN HYBRID. RACE ROXKIT.', 540, 1805);

  ctx.fillStyle = '#94a3b8';
  ctx.font = "500 18px 'Outfit', 'Inter', sans-serif";
  ctx.fillText('roxkit.ndclabs.app', 540, 1845);

  if (options.asBlob) {
    if (typeof canvas.convertToBlob === 'function') {
      return canvas.convertToBlob({ type: 'image/png' });
    }
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png');
    });
  }

  return canvas;
}
